/**
 * Integrytas Group - Common Security Module (CSM)
 * Maps csm_page_master.internal_code -> the React component that renders the page.
 *
 * Add new entries here when new pages are added to PAGE_REGISTRY.
 */

import type { ComponentType } from 'react';
import type { AuthSession } from '../types/auth';
import { UserListPage } from './users/UserListPage';
import { RoleListPage } from './roles/RoleListPage';
import { PageMasterPage } from './admin/PageMasterPage';
import { CsmApplicationPage } from './admin/CsmApplicationPage';
import { JobDescriptionPage } from './admin/JobDescriptionPage';
import { ApprovalHierarchyPage } from './admin/ApprovalHierarchyPage';
import { UserProvisionPage } from './users/UserProvisionPage';
import { SystemParametersPage } from './admin/SystemParametersPage';
import { I18nManagerPage } from './admin/I18nManagerPage';
import { I18nNewLanguagePage } from './admin/I18nNewLanguagePage';
import { AuditLogPage }         from './admin/AuditLogPage';
import { ApprovalConfigPage }   from './admin/ApprovalConfigPage';
import { DataQualityRulesPage } from './admin/DataQualityRulesPage';
import { PdeDashboardPage }    from './admin/PdeDashboardPage';
import { OlhManagerPage }      from './admin/OlhManagerPage';
import { MtaRuleManagerPage }  from './admin/MtaRuleManagerPage';
import { MtaTasksPage }        from './admin/MtaTasksPage';
import { PfwPortalManagerPage } from './admin/PfwPortalManagerPage';
import { PfwMembershipPage }    from './admin/PfwMembershipPage';
import { OdsSyncManagerPage }   from './admin/OdsSyncManagerPage';
import { OdsConflictsPage }     from './admin/OdsConflictsPage';
import { DocTemplateManagerPage } from './admin/DocTemplateManagerPage';
import { DocGeneratedPage } from './doc/DocGeneratedPage';
import { DocFormatMaskPage, DocPageSizePage, DocResourcePage } from './doc/DocLookupPages';
import { ReportDesignerPage } from './rpd/ReportDesignerPage';
import { DpcPolicyPage }       from './admin/DpcPolicyPage';
import { DpcRequestsPage }     from './admin/DpcRequestsPage';
import { IamProviderManagerPage } from './admin/IamProviderManagerPage';
import { EcmTemplateManagerPage } from './admin/EcmTemplateManagerPage';
import { UppDefinitionManagerPage } from './admin/UppDefinitionManagerPage';
import { UneAdminPage }        from './admin/UneAdminPage';
import { UnePreferenceCenterPage } from './admin/UnePreferenceCenterPage';
import { DapPolicyManagerPage } from './admin/DapPolicyManagerPage';
import { GtcTrashPage }       from './admin/GtcTrashPage';
import { AsfDiagnosticsPage } from './admin/AsfDiagnosticsPage';
import { ArlConfigPage }     from './admin/ArlConfigPage';
import { UatTimelinePage }   from './admin/UatTimelinePage';
import { DffFlagsPage }      from './admin/DffFlagsPage';
import { CmmLocksPage }      from './admin/CmmLocksPage';
import { AdiAssertionsPage } from './admin/AdiAssertionsPage';
import { ApgTelemetryPage } from './admin/ApgTelemetryPage';
import { IsrTargetsPage }   from './admin/IsrTargetsPage';
import { GsiIndexerPage }   from './admin/GsiIndexerPage';
import { FdmDependencyMapPage } from './admin/FdmDependencyMapPage';
import { CbmBreakersPage }    from './admin/CbmBreakersPage';
import { IkeKeysPage }        from './admin/IkeKeysPage';
import { DseRegistryPage }    from './admin/DseRegistryPage';
import { DdmRefreshPage }     from './admin/DdmRefreshPage';
import { UdcLayoutPage }      from './admin/UdcLayoutPage';
import { DseReportingPage }   from './admin/DseReportingPage';
import { CdsDatasetListPage } from './admin/CdsDatasetListPage';
import { PfwPortalAdminPage } from './admin/PfwPortalAdminPage';
import { PheHooksPage }       from './admin/PheHooksPage';
import { UldOverridesPage }   from './admin/UldOverridesPage';
import { DadExplorerPage }    from './admin/DadExplorerPage';
import { FlnFieldSourcePage } from './admin/FlnFieldSourcePage';
import { RorRoutingPage }     from './admin/RorRoutingPage';
import { LicStatusPage }      from './admin/LicStatusPage';
import { MyApprovalsPage }      from './admin/MyApprovalsPage';
import { ApprovalDelegationsPage } from './admin/ApprovalDelegationsPage';
import { ApprovalAnalyticsPage } from './admin/ApprovalAnalyticsPage';
import { ApprovalSlaAuthorityPage } from './admin/ApprovalSlaAuthorityPage';
import { AiConfigPage }         from './admin/AiConfigPage';
import { AssetUploadPage } from './admin/AssetUploadPage';
import { BulkImportPage } from './admin/BulkImportPage';
import {
  CompanyPage,
  DivisionPage,
  BusinessUnitPage,
  OfficePage,
} from './admin/OrgLookupPage';
import { CmsPageListPage }     from './cms/CmsPageListPage';
import { CmsAssetLibraryPage } from './cms/CmsAssetLibraryPage';
import { EdsTemplateListPage } from './eds/EdsTemplateListPage';
import { EdsCampaignListPage } from './eds/EdsCampaignListPage';
import { EdsRecipientPage }    from './eds/EdsRecipientPage';
import { EdsEmailLogPage }     from './eds/EdsEmailLogPage';
import { EdsOptOutPage }       from './eds/EdsOptOutPage';
import { EdsDashboardPage }    from './eds/EdsDashboardPage';
import { DmsDocumentsPage }    from './dms/DmsDocumentsPage';
import { FxrCurrencyPage, FxrCurrencyRatePage } from './fxr/FxrReferencePages';
// Per-application component map (owned by derived apps; empty in BaseApp).
import { APP_PAGE_COMPONENTS } from '../app/app_page_components';
// DMO (Demo Product Catalog) — BaseApp's own reference module, isolated so a
// derived app can delete src/pages/dmo/ and this one import line cleanly (§16).

export interface PageComponentProps {
  session: AuthSession;
  onBeginMimic?: (userId: number) => void;
}

export const PAGE_COMPONENTS: Record<string, ComponentType<PageComponentProps>> = {
  // CSM — Core Security Module
  'CSM.USER.LIST':          UserListPage,
  'CSM.ROLE.LIST':          RoleListPage,
  'CSM.PAGE.MASTER':        PageMasterPage,
  'CSM.APPLICATION.LIST':   CsmApplicationPage,
  'CSM.JOB.LIST':           JobDescriptionPage,
  'CSM.HIERARCHY.LIST':     ApprovalHierarchyPage,
  'CSM.USER.PROVISION':     UserProvisionPage,
  'CSM.SYSTEM.PARAMETERS':  SystemParametersPage,
  'CSM.I18N.MANAGER':       I18nManagerPage,
  'CSM.I18N.NEW_LANGUAGE':  I18nNewLanguagePage,
  'CSM.SYSTEM.AUDIT_LOG':   AuditLogPage,
  'APR.CONFIG':   ApprovalConfigPage,
  'DQE.RULES':    DataQualityRulesPage,
  'PDE.DASHBOARD':   PdeDashboardPage,
  'OLH.MANAGER':     OlhManagerPage,
  'MTA.RULES':       MtaRuleManagerPage,
  'MTA.TASKS':       MtaTasksPage,
  'PFW.PORTALS':     PfwPortalManagerPage,
  'PFW.MEMBERSHIP':  PfwMembershipPage,
  'PFW.PORTAL_ADMIN': PfwPortalAdminPage,
  'ODS.SYNC':        OdsSyncManagerPage,
  'ODS.CONFLICTS':   OdsConflictsPage,
  'DOC.TEMPLATES':   DocTemplateManagerPage,
  'DOC.GENERATED':   DocGeneratedPage,
  'RPD.DESIGNER':    ReportDesignerPage,
  'DOC.FORMAT_MASKS': DocFormatMaskPage,
  'DOC.PAGE_SIZES':   DocPageSizePage,
  'DOC.RESOURCES':    DocResourcePage,
  'DPC.POLICY':      DpcPolicyPage,
  'DPC.REQUESTS':    DpcRequestsPage,
  'IAM.PROVIDERS':   IamProviderManagerPage,
  'ECM.TEMPLATES':   EcmTemplateManagerPage,
  'UPP.DEFINITIONS': UppDefinitionManagerPage,
  'UNE.ADMIN':       UneAdminPage,
  'UNE.PREFERENCES': UnePreferenceCenterPage,
  'DAP.POLICIES':    DapPolicyManagerPage,
  'GTC.TRASH':       GtcTrashPage,
  'ASF.DIAGNOSTICS': AsfDiagnosticsPage,
  'ARL.CONFIG':      ArlConfigPage,
  'UAT.TIMELINE':    UatTimelinePage,
  'DFF.FLAGS':       DffFlagsPage,
  'CMM.LOCKS':       CmmLocksPage,
  'ADI.WATCHDOG':    AdiAssertionsPage,
  'APG.TELEMETRY':   ApgTelemetryPage,
  'ISR.TARGETS':     IsrTargetsPage,
  'GSI.INDEXER':     GsiIndexerPage,
  'FDM.MAP':         FdmDependencyMapPage,
  'CBM.BREAKERS':    CbmBreakersPage,
  'IKE.KEYS':        IkeKeysPage,
  'DSE.REGISTRY':    DseRegistryPage,
  'DDM.REFRESH':     DdmRefreshPage,
  'UDC.LAYOUT':      UdcLayoutPage,
  'DSE.REPORTING':   DseReportingPage,
  'CDS.DATASET.LIST': CdsDatasetListPage,
  'PHE.HOOKS':       PheHooksPage,
  'ULD.OVERRIDES':   UldOverridesPage,
  'DAD.EXPLORER':    DadExplorerPage,
  'FLN.FIELD_SOURCE': FlnFieldSourcePage,
  'ROR.ROUTING':     RorRoutingPage,
  'LIC.STATUS':      LicStatusPage,
  'APR.INBOX': MyApprovalsPage,
  'APR.DELEGATIONS':   ApprovalDelegationsPage,
  'APR.ANALYTICS':     ApprovalAnalyticsPage,
  'APR.SLA_AUTHORITY': ApprovalSlaAuthorityPage,
  'CSM.SYSTEM.AI_CONFIG':   AiConfigPage,
  'CSM.SYSTEM.ASSETS':      AssetUploadPage,
  'CSM.SYSTEM.BULK_IMPORT': BulkImportPage,
  'CSM.ORG.COMPANY':        CompanyPage,
  'CSM.ORG.DIVISION':       DivisionPage,
  'CSM.ORG.BUSINESS_UNIT':  BusinessUnitPage,
  'CSM.ORG.OFFICE':         OfficePage,
  // CMS — Content Management System
  'CMS.PAGE.LIST':          CmsPageListPage,
  'CMS.ASSET.LIBRARY':      CmsAssetLibraryPage,
  // EDS — Email Distribution System
  'EDS.DASHBOARD':          EdsDashboardPage,
  'EDS.TEMPLATE.LIST':      EdsTemplateListPage,
  'EDS.CAMPAIGN.LIST':      EdsCampaignListPage,
  'EDS.RECIPIENT.LIST':     EdsRecipientPage,
  'EDS.EMAIL.LOG':          EdsEmailLogPage,
  'EDS.OPT.OUT':            EdsOptOutPage,
  // DMS — Document Management System
  'DMS.DOCUMENTS':          DmsDocumentsPage,
  // FXR — Foreign Exchange Rates
  'FXR.CURRENCY.LIST':      FxrCurrencyPage,
  'FXR.RATE.LIST':          FxrCurrencyRatePage,
  // DMO — Demo Product Catalog (reference module, stripped for derived apps)
  // Per-application pages (owned by derived apps; empty in BaseApp)
  ...APP_PAGE_COMPONENTS,
};
